import React, { useRef, useState,useEffect } from 'react';
import { ApolloClient, InMemoryCache, useQuery, gql } from '@apollo/client';
import styled from 'styled-components';

import {useCart} from '@shopify/hydrogen-react';




import OptionsSearchResults from './searchOptionResults';
import { sendTrainingOptionEvent } from './posTrainingEvents.js';

const ResultsShell = styled.section`
  background: #ffffff;
  border: 1px solid #d7ded6;
  border-radius: 8px;
  overflow: hidden;
`;

const ResultsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 13px 14px;
  border-bottom: 1px solid #e4e9e6;
  background: #f9faf8;

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const ResultsTitle = styled.h3`
  margin: 0;
  color: #18251d;
  font-size: 1rem;
`;

const ResultsMeta = styled.div`
  color: #667085;
  font-size: 0.86rem;
  font-weight: 650;
`;

const ProductList = styled.div`
  display: grid;
  gap: 10px;
  padding: 12px;
`;

const ProductCard = styled.article`
  border: 1px solid #dce3df;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
`;

const ProductHeader = styled.div`
  padding: 12px;
  border-bottom: 1px solid #eef1ef;
  background: #fbfcfb;
`;

const ProductLink = styled.a`
  color: #17251d;
  text-decoration: none;
  font-size: 1.05rem;
  font-weight: 800;
  line-height: 1.25;

  &:hover {
    color: #1b755f;
  }
`;

const ProductVendor = styled.div`
  color: #667085;
  font-size: 0.78rem;
  font-weight: 700;
  margin-top: 3px;
`;

const VariantGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(290px, 1fr));
  gap: 10px;
  padding: 10px;
`;

const VariantPanel = styled.div`
  border: 1px solid #dde5e0;
  border-radius: 8px;
  background: #fcfdfc;
  padding: 10px;
  display: grid;
  gap: 10px;
  align-content: start;
`;

const VariantTopRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
`;

const VariantName = styled.div`
  color: #1f2933;
  font-size: 0.98rem;
  font-weight: 850;
  line-height: 1.2;
  min-width: 0;
  overflow-wrap: anywhere;
  text-transform: uppercase;
`;

const QuantityCluster = styled.div`
  display: inline-grid;
  grid-template-columns: 36px 42px 36px;
  align-items: center;
  border: 1px solid #cbd6d0;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
`;

const QuantityButton = styled.button`
  width: 36px;
  height: 36px;
  border: 0;
  background: #edf3f0;
  color: #1e3b31;
  font-size: 1.2rem;
  font-weight: 900;
  cursor: pointer;

  &:hover {
    background: #dcebe5;
  }
`;

const QuantityInput = styled.input`
  width: 42px;
  height: 36px;
  border: 0;
  border-left: 1px solid #cbd6d0;
  border-right: 1px solid #cbd6d0;
  background: #ffffff;
  color: #1f2933;
  text-align: center;
  font-size: 0.98rem;
  font-weight: 800;
  box-sizing: border-box;

  &:focus {
    outline: 2px solid rgba(34, 124, 104, 0.25);
    outline-offset: -2px;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  & {
    -moz-appearance: textfield;
  }
`;

const SelectedOptionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 30px;
`;

const SelectedOptionChip = styled.button`
  border: 1px solid #e0b4a9;
  border-radius: 999px;
  background: #fff1ed;
  color: #8a321f;
  padding: 5px 9px;
  font-size: 0.82rem;
  font-weight: 800;
  cursor: pointer;
  max-width: 100%;
  overflow-wrap: anywhere;

  &:hover {
    background: #ffe3db;
  }
`;

const EmptyOptionsLine = styled.div`
  color: #829089;
  font-size: 0.84rem;
  font-weight: 650;
  padding: 5px 0;
`;

const RequestSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const RequestOption = styled.label`
  position: relative;
  display: inline-flex;
`;

const RadioButton = styled.input.attrs({ type: 'radio' })`
  position: absolute;
  opacity: 0;
  pointer-events: none;

  &:checked + span {
    background: #1f6f5d;
    border-color: #1f6f5d;
    color: #ffffff;
  }

  &:focus-visible + span {
    box-shadow: 0 0 0 3px rgba(31, 111, 93, 0.18);
  }
`;

const RequestLabel = styled.span`
  min-height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #cbd6d0;
  border-radius: 999px;
  background: #ffffff;
  color: #344054;
  padding: 0 10px;
  font-size: 0.78rem;
  font-weight: 850;
  cursor: pointer;
  white-space: nowrap;
`;

const OptionSearchRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
`;

const SearchBox = styled.input`
  min-height: 40px;
  width: 100%;
  border: 1px solid #c9d4cd;
  border-radius: 8px;
  background: #ffffff;
  color: #1f2933;
  padding: 0 11px;
  font-size: 0.94rem;
  font-weight: 700;
  box-sizing: border-box;
  outline: none;

  &:focus {
    border-color: #227c68;
    box-shadow: 0 0 0 3px rgba(34, 124, 104, 0.14);
  }

  &::placeholder {
    color: #8a968f;
    font-weight: 500;
  }
`;

const ClearButton = styled.button`
  min-width: 40px;
  min-height: 40px;
  border: 1px solid #c9d4cd;
  border-radius: 8px;
  background: #ffffff;
  color: #52635b;
  font-size: 1rem;
  font-weight: 900;
  cursor: pointer;

  &:hover {
    background: #f0f4f2;
  }

  &:disabled {
    color: #9aa6a0;
    background: #f5f7f6;
    cursor: default;
  }
`;

const AddToCartButton = styled.button`
  min-height: 42px;
  border: 0;
  border-radius: 8px;
  background: #21816b;
  color: #ffffff;
  padding: 0 14px;
  font-size: 0.92rem;
  font-weight: 900;
  cursor: pointer;
  width: 100%;

  &:hover {
    background: #176f5b;
  }

  &:active {
    transform: translateY(1px);
  }

  &[disabled] {
    background: #aab5af;
    cursor: not-allowed;
  }
`;

const EmptyResults = styled.div`
  min-height: 220px;
  display: grid;
  place-items: center;
  color: #607069;
  text-align: center;
  padding: 28px;
`;

const EmptyResultsTitle = styled.h3`
  margin: 0 0 5px;
  color: #283a31;
  font-size: 1.12rem;
`;

const EmptyResultsText = styled.p`
  margin: 0;
  max-width: 360px;
  font-size: 0.92rem;
  line-height: 1.45;
`;

// Initialize Apollo Client
const client = new ApolloClient({
    uri: 'https://vasispos.myshopify.com/api/2023-07/graphql.json',
    headers: {
      'X-Shopify-Storefront-Access-Token': 'fdce90b66037af30f00eb1885dfb35b7'
    },
    cache: new InMemoryCache()
});
  
const SEARCH_PRODUCTS = gql`
  query predictiveSearch($query: String!) {
    predictiveSearch(query: $query, searchableFields: [VENDOR, TAG, TITLE], types: [PRODUCT], unavailableProducts: SHOW) {
      products {
        id
        tags  
        handle
        title
        vendor
        variants(first: 50) { 
          edges {
            node {
              title
              id
            }
          }
        }
      }
    }
  }
`;

const OPTION_FILTERS = [
  { value: '', label: 'All' },
  { value: 'Add', label: 'Add' },
  { value: 'No', label: 'No' },
  { value: 'LESS', label: 'Less' },
  { value: 'Only', label: 'Only' },
  { value: 'EXTRA SAUCE', label: 'Extra Sauce' },
  { value: 'On the Side', label: 'Side' },
  { value: 'USE DIFFERENT SAUCE', label: 'Sauce' },
  { value: 'SPECIAL INSTRUCTION', label: 'Note' }
];

const countVariants = (items) => {
  return items.reduce((count, product) => count + (product.variants?.edges?.length || 0), 0);
};
  

function PredictiveSearch({
  vendorFilter,
  searchTerm,
  rawSearchTerm = '',
  handleSuccessAddToCart,
  trainingSessionId,
  traineeId,
  restaurantName,
  mainSearchStartedAt
}) {
  
  const cart = useCart();
  


  const { status, linesAdd, lines ,checkoutUrl, error: cartError} = cart;



  useEffect(() => {
    console.log('useCart status', status);
  }, [status]);



  useEffect(() => {
    console.log('lines changed:', lines);
  }, [lines]); // Dependency array with 'lines'
  
  useEffect(() => {
    console.log('checkoutUrl changed:', checkoutUrl);
  }, [checkoutUrl]); // Dependency array with 'lines'




  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [products, setProducts] = useState([]);
  
  const [quantities, setQuantities] = useState({});

  const [optionSearchTerms, setOptionSearchTerms] = useState({});


  const [selectedOptions, setSelectedOptions] = useState({});

  const [searchTermWithVendorAddsSlectedOptions,setSearchTermWithVendorAddsSlectedOptions] = useState({});
  
  const [selectedOptionAttributes, setSelectedOptionAttributes] = useState({});
  const optionSearchStartedAtRef = useRef({});

  // DEBUG: Log the query being sent to Shopify
  console.log('🔍 SHOPIFY QUERY:', {
    debouncedSearchTerm,
    searchTerm,
    vendorFilter
  });

  const { data, loading, error } = useQuery(SEARCH_PRODUCTS, {
    variables: { query: debouncedSearchTerm },
    client: client
  });

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 5);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  useEffect(() => {
    // DEBUG: Log raw response from Shopify
    console.log('🔍 SHOPIFY RESPONSE:', {
      hasData: !!data,
      productsCount: data?.predictiveSearch?.products?.length || 0,
      rawProducts: data?.predictiveSearch?.products?.slice(0, 3) // First 3 for preview
    });

    if (data && data.predictiveSearch && data.predictiveSearch.products) {
      let products = data.predictiveSearch.products;

      // Exclude products with tags containing specific substrings, ignoring case
      products = products.filter(product => 
        !product.tags.some(tag => ["option", "less", "no", "extra", "add",].some(excludedTag => tag.toLowerCase() === excludedTag.toLowerCase()))
      );

      // Filter out the variants' titles that do not include # only if variants exist
      products = products.map(product => {
          if (product.variants && product.variants.edges && product.variants.edges.length >= 5) {
          return {
              ...product,
              variants: {
              ...product.variants,
              edges: product.variants.edges.filter(variant => 
                  variant.node.title.toLowerCase().includes('#') ||
                  variant.node.title.toLowerCase().includes('default') ||
                  variant.node.title.toLowerCase().includes('small') ||
                  variant.node.title.toLowerCase().includes('large') ||
                  (variant.node.title.toLowerCase().includes('piece') && variant.node.title.toLowerCase().includes('plain') && !variant.node.title.toLowerCase().includes('rice') && !variant.node.title.toLowerCase().includes('lo mein')) ||
                  variant.node.title.toLowerCase() === 'plain' ||
                  variant.node.title.toLowerCase() === 'pint' ||
                  variant.node.title.toLowerCase() === 'cup' ||
                  variant.node.title.toLowerCase() === 'pt' ||
                  variant.node.title.toLowerCase() === 'quart'
              )
              }
          };
          } else {
          return product;
          }
      });

      // Sort products so that products containing "On the Side" come first, ignoring case
      products.sort((a, b) => (a.title.toLowerCase().includes("on the side") ? -1 : b.title.toLowerCase().includes("on the side") ? 1 : 0));

      setProducts(products);
      sendTrainingOptionEvent({
        eventType: 'main_results',
        searchKind: 'main',
        sessionId: trainingSessionId,
        traineeId,
        restaurantName,
        query: rawSearchTerm || searchTerm,
        resultCount: products.length,
        elapsedMs: mainSearchStartedAt ? Date.now() - mainSearchStartedAt : null,
      });
    }
  }, [data, mainSearchStartedAt, rawSearchTerm, restaurantName, searchTerm, traineeId, trainingSessionId]);

  const resultCount = countVariants(products);

  if (error) {
    return (
      <ResultsShell>
        <ResultsHeader>
          <ResultsTitle>Menu Results</ResultsTitle>
          <ResultsMeta>Search unavailable</ResultsMeta>
        </ResultsHeader>
        <EmptyResults>
          <div>
            <EmptyResultsTitle>Search error</EmptyResultsTitle>
            <EmptyResultsText>{error.message}</EmptyResultsText>
          </div>
        </EmptyResults>
      </ResultsShell>
    );
  }

  function handleQuantityChange(event, variantId) {
      setQuantities({
          ...quantities,
          [variantId]: event.target.value
      });
  }
  
  function handleIncrement(variantId) {
      setQuantities({
          ...quantities,
          [variantId]: (quantities[variantId] || 1) + 1
      });
  }
  
  function handleDecrement(variantId) {
      setQuantities({
          ...quantities,
          [variantId]: quantities[variantId] > 1 ? quantities[variantId] - 1 : 1
      });
  }
  


   


  const handleOptionSelected = (productVariantId, optionVariantTitle, optionVariantId, optionProductTitle) => {
    console.log('handleOptionSelected called with:', { productVariantId, optionVariantTitle, optionVariantId, optionProductTitle });
    setSelectedOptionAttributes(prevState => {
      const existingOptions = prevState[productVariantId] || [];
      const newOption = { optionVariantTitle, optionVariantId, optionProductTitle };
  
      // Check if the option is already selected
      const isOptionSelected = existingOptions.some(option => option.optionVariantId === newOption.optionVariantId);
  
      if (isOptionSelected) {
        // Option is already selected, remove it from the array
        return {
          ...prevState,
          [productVariantId]: existingOptions.filter(option => option.optionVariantId !== newOption.optionVariantId)
        };
      } else {
        // Option is not selected, add it to the array
        return {
          ...prevState,
          [productVariantId]: [...existingOptions, newOption]
        };
      }
    });
  };


  function handleAddToCart(variantId, productTitle, productVariantTitle) {
    const quantity = quantities[variantId] || 1; // Get the quantity from the state
    const selectedOption = selectedOptionAttributes[variantId];
    let mainLineAttributes = [];
    let lines = [];
    
    
    
    
    // If selected options are available, convert them to an array of key-value objects
    if (selectedOption) {

      // Generate a random 13-digit number
      const randomNum = Math.floor(Math.random() * (9e12)) + 1e12;

      mainLineAttributes.push({
        key: "_io_order_group",
        value: randomNum.toString() // Convert the number to a string
      });


      selectedOption.forEach(option => {
        mainLineAttributes.push({
          key: option.optionVariantTitle,
          value: option.optionVariantId
        });

        const line = {
          merchandiseId: option.optionVariantId,
          quantity: 1, // Set quantity to 1 or any other value as needed
          attributes: [
            {
              key: "_io_parent_order_group",
              value: randomNum.toString() // Convert the number to a string
            }
          ]
        };
        lines.push(line);

      });

    }


    
    const mainLine = { 
      merchandiseId: variantId, 
      quantity: quantity,
      attributes: mainLineAttributes, // Use the key-value object as attributes
    };

    lines.push(mainLine);

    sendTrainingOptionEvent({
      eventType: 'product_added',
      searchKind: 'main',
      sessionId: trainingSessionId,
      traineeId,
      restaurantName,
      query: rawSearchTerm || searchTerm,
      elapsedMs: mainSearchStartedAt ? Date.now() - mainSearchStartedAt : null,
      productTitle,
      productVariantTitle,
      productVariantId: variantId,
    });



    linesAdd(lines); // Pass an array of lines to linesAdd
    
    if (cartError) {
      console.log('cartError',cartError);
      return <p>Error adding to cart: {cartError.message}</p>;
    }
    handleSuccessAddToCart('');
  }

 

  function handleOptionSearchInputChange(event, variantId) {
    const nextValue = event.target.value;
    setOptionSearchTerms({
      ...optionSearchTerms,
      [variantId]: nextValue
    });

    const trimmed = nextValue.trim();
    if (trimmed && !optionSearchTerms[variantId]?.trim()) {
      optionSearchStartedAtRef.current[variantId] = Date.now();
      sendTrainingOptionEvent({
        eventType: 'option_search_started',
        searchKind: 'option',
        sessionId: trainingSessionId,
        traineeId,
        restaurantName,
        query: trimmed,
        productVariantId: variantId,
        selector: selectedOptions[variantId] || '',
      });
    } else if (!trimmed) {
      delete optionSearchStartedAtRef.current[variantId];
    }
  
    if (nextValue) {
      setSearchTermWithVendorAddsSlectedOptions({
        ...searchTermWithVendorAddsSlectedOptions,
        [variantId]: selectedOptions[variantId] ? vendorFilter + " " + selectedOptions[variantId] + " " + nextValue : vendorFilter + " " + nextValue 
      });
    } else {
      setSearchTermWithVendorAddsSlectedOptions({
        ...searchTermWithVendorAddsSlectedOptions,
        [variantId]: ''
      });
    }
  }

 



  function handleOptionChange(event, variantId) {
    setSelectedOptions({
        ...selectedOptions,
        [variantId]: event.target.value
    });


    setSearchTermWithVendorAddsSlectedOptions({
      ...searchTermWithVendorAddsSlectedOptions,
      [variantId]: optionSearchTerms[variantId] ? vendorFilter + " " + event.target.value + " " + optionSearchTerms[variantId] : vendorFilter + " " + event.target.value 
    });
    
    

  }


  return (
    <ResultsShell>
      <ResultsHeader>
        <ResultsTitle>Menu Results</ResultsTitle>
        <ResultsMeta>
          {loading ? 'Searching...' : `${products.length} item${products.length === 1 ? '' : 's'} / ${resultCount} choice${resultCount === 1 ? '' : 's'}`}
        </ResultsMeta>
      </ResultsHeader>

      {loading && (
        <EmptyResults role="status">
          <div>
            <EmptyResultsTitle>Searching menu</EmptyResultsTitle>
            <EmptyResultsText>{rawSearchTerm || searchTerm}</EmptyResultsText>
          </div>
        </EmptyResults>
      )}

      {!loading && products.length === 0 && (
        <EmptyResults>
          <div>
            <EmptyResultsTitle>No menu matches</EmptyResultsTitle>
            <EmptyResultsText>{rawSearchTerm || searchTerm}</EmptyResultsText>
          </div>
        </EmptyResults>
      )}

      {!loading && products.length > 0 && (
        <ProductList>
          {products.map(product => (
            <ProductCard key={product.handle}>
              <ProductHeader>
                <ProductLink href={`https://vasispos.myshopify.com/products/${product.handle}`} target="_blank" rel="noopener noreferrer">
                  {product.title}
                </ProductLink>
                <ProductVendor>{product.vendor}</ProductVendor>
              </ProductHeader>

              <VariantGrid>
                {product.variants.edges.map(variant => {
                  const productVariantId = variant.node.id;
                  let variantTitle = variant.node.title;
                  if (variantTitle === "Default Title") {
                    variantTitle = "";
                  }
                  const variantLabel = variantTitle || 'Default';
                  const selectedForVariant = selectedOptionAttributes[productVariantId] || [];

                  return (
                    <VariantPanel key={productVariantId}>
                      <VariantTopRow>
                        <VariantName>{variantLabel}</VariantName>
                        <QuantityCluster>
                          <QuantityButton data-action="decrement" onClick={() => handleDecrement(productVariantId)} aria-label="Decrease quantity">-</QuantityButton>
                          <QuantityInput type="number" min="1" value={quantities[productVariantId] || 1} onChange={(event) => handleQuantityChange(event, productVariantId)} />
                          <QuantityButton data-action="increment" onClick={() => handleIncrement(productVariantId)} aria-label="Increase quantity">+</QuantityButton>
                        </QuantityCluster>
                      </VariantTopRow>

                      <SelectedOptionsRow>
                        {selectedForVariant.length > 0 ? selectedForVariant.map(option => (
                          <SelectedOptionChip
                            key={option.optionVariantId}
                            type="button"
                            onClick={() => handleOptionSelected(productVariantId, option.optionVariantTitle, option.optionVariantId, product.title)}
                          >
                            x {option.optionVariantTitle || option.optionProductTitle}
                          </SelectedOptionChip>
                        )) : (
                          <EmptyOptionsLine>No requests selected</EmptyOptionsLine>
                        )}
                      </SelectedOptionsRow>

                      <RequestSelector>
                        {OPTION_FILTERS.map(option => (
                          <RequestOption key={option.label}>
                            <RadioButton
                              value={option.value}
                              name={productVariantId}
                              checked={(selectedOptions[productVariantId] || '') === option.value}
                              onChange={(event) => handleOptionChange(event, productVariantId)}
                            />
                            <RequestLabel>{option.label}</RequestLabel>
                          </RequestOption>
                        ))}
                      </RequestSelector>

                      <OptionSearchRow>
                        <SearchBox
                          type="text"
                          value={optionSearchTerms[productVariantId] || ''}
                          onChange={(event) => handleOptionSearchInputChange(event, productVariantId)}
                          placeholder="Request, sauce, side, note"
                          autoComplete="off"
                        />
                        {optionSearchTerms[productVariantId] ? (
                          <ClearButton type="button" onClick={() => handleOptionSearchInputChange({ target: { value: '' } }, productVariantId)} aria-label="Clear request search">
                            x
                          </ClearButton>
                        ) : (
                          <ClearButton type="button" disabled aria-label="No request search">
                            +
                          </ClearButton>
                        )}
                      </OptionSearchRow>

                      {searchTermWithVendorAddsSlectedOptions[productVariantId] !== undefined && searchTermWithVendorAddsSlectedOptions[productVariantId].length > 0 && (
                        <OptionsSearchResults
                          productVariantId={productVariantId}
                          productTitle={product.title}
                          searchTermWithVendorAddsSlectedOptions={searchTermWithVendorAddsSlectedOptions[productVariantId]}
                          optionSearchTerms={optionSearchTerms[productVariantId] || ''}
                          handleOptionSelected={handleOptionSelected}
                          selectedOptionAttributes={selectedOptionAttributes}
                          client={client}
                          trainingSessionId={trainingSessionId}
                          traineeId={traineeId}
                          restaurantName={restaurantName}
                          selector={selectedOptions[productVariantId] || ''}
                          optionSearchStartedAt={optionSearchStartedAtRef.current[productVariantId] || null}
                        />
                      )}

                      <AddToCartButton data-id={productVariantId} onClick={() => handleAddToCart(productVariantId, product.title, variantTitle)}>
                        Add Item
                      </AddToCartButton>
                    </VariantPanel>
                  );
                })}
              </VariantGrid>
            </ProductCard>
          ))}
        </ProductList>
      )}
    </ResultsShell>
  );
}



export default PredictiveSearch;
